/**
 * The legal identity behind Easy Invoices.
 *
 * Two consumers need the same facts and must never drift apart:
 *
 * - the AVV / DPA PDF (`apps/api/src/app/shop/shop-avv.service.ts`), where the block names the
 *   **Auftragsverarbeiter** under Art. 28 DSGVO, and
 * - the footer of every Easy-Invoices-branded email (`libs/jobs/src/lib/constants/
 *   email-brand.constants.ts`), which carries the Anbieterkennzeichnung a commercial mail owes
 *   under § 5 DDG / § 18 Abs. 2 MStV. **DDG, not TMG:** the Telemediengesetz was repealed on
 *   2024-05-14 and its provider-identification duty moved unchanged into § 5 des Digitale-Dienste-
 *   Gesetzes. Same facts, different citation — do not "correct" it back.
 *
 * Both used to hold their own copy of the street address. They now read this one.
 *
 * The source of truth for the wording is the published Impressum
 * (`apps/landing-page/src/pages/impressum.astro`) — change it there and here together.
 */
export const COMPANY_IDENTITY = {
  /** Registered name, as it appears in the Impressum. */
  legalName: 'A&C Finance Solutions GbR',
  /** The product this company publishes. Not a legal name — the brand a merchant recognises. */
  productName: 'Easy Invoices',
  /** Vertretungsberechtigte, comma-separated for prose use. */
  representatives: 'Erik Arens, Jan Carl Werner Cwiklinski',
  street: 'Bamenohler Str. 34',
  postalCode: '57413',
  city: 'Finnentrop',
  /** German spelling; `countryEn` is the English one. Neither is a country CODE. */
  country: 'Deutschland',
  countryEn: 'Germany',
  phone: '02721/6002144',
  /** General contact, as published in the Impressum. */
  email: 'kontakt@a-and-c.de',
  /** Data-protection contact. This is the address the AVV names, not the general one. */
  privacyEmail: 'datenschutz@easy-invoices.de',
  /** USt-IdNr. per § 27a UStG. */
  vatId: 'DE 455533462',
  website: 'https://easy-invoices.de',
} as const;

/**
 * The postal address on one line, in the order a German reader expects.
 *
 * @param locale - `de` picks "Deutschland", anything else picks "Germany".
 * @returns e.g. `Bamenohler Str. 34, 57413 Finnentrop, Deutschland`
 */
export function formatCompanyAddress(locale: string): string {
  const country =
    locale === 'de' ? COMPANY_IDENTITY.country : COMPANY_IDENTITY.countryEn;
  return `${COMPANY_IDENTITY.street}, ${COMPANY_IDENTITY.postalCode} ${COMPANY_IDENTITY.city}, ${country}`;
}

/**
 * The no-tax-or-legal-advice notice — the short form of AGB § 2a ("Keine Steuer- oder
 * Rechtsberatung; Prüfpflicht des Nutzers"), per language, as short lines.
 *
 * One wording for every place the team talks to merchants in its own voice: the email signature
 * (`apps/dashboard/src/lib/email-signature/signature.ts`), the support-desk reply mail
 * (`libs/jobs/src/lib/services/support-email.service.ts`) and the in-app ticket thread
 * (`apps/easy-invoices/app/routes/app.support.$id.tsx`). The team answers tax questions there
 * every day, and the notice only protects anyone if it reads the same under each of them.
 *
 * Kept as LINES because the signature prints them broken — its table's natural width has to fit a
 * phone — while prose surfaces join them with a space (see {@link noAdviceDisclaimerText}). Each
 * line stays at 64 characters or fewer. If AGB § 2a changes in substance, change this with it.
 */
export const NO_ADVICE_DISCLAIMER_LINES = {
  de: [
    'Hinweis: Wir sind weder Steuerberater noch Rechtsanwälte.',
    'Unsere Auskünfte stellen keine Steuer- oder Rechtsberatung dar',
    'und ersetzen nicht die Prüfung durch Ihre Steuerberatung.',
  ],
  en: [
    'Note: We are neither tax advisors nor lawyers.',
    'Our information does not constitute tax or legal advice',
    'and does not replace a review by your own tax advisor.',
  ],
} as const satisfies Record<'de' | 'en', readonly string[]>;

/**
 * The no-advice notice as lines.
 *
 * @param locale - `de` picks German, anything else English (the same rule as
 *   {@link formatCompanyAddress}).
 */
export function noAdviceDisclaimerLines(locale: string): readonly string[] {
  return locale === 'de'
    ? NO_ADVICE_DISCLAIMER_LINES.de
    : NO_ADVICE_DISCLAIMER_LINES.en;
}

/** The no-advice notice as one paragraph, for surfaces that wrap text themselves. */
export function noAdviceDisclaimerText(locale: string): string {
  return noAdviceDisclaimerLines(locale).join(' ');
}
